import { useQuery } from '@tanstack/react-query'
import { api } from '@/lib/api'
import { PageHeader } from '@/components/layout/PageHeader'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card'
import { Badge } from '@/components/ui/Badge'
import { Link } from 'react-router-dom'
import { Button } from '@/components/ui/Button'
import type { DashboardMetrics } from '@/types'

export function SupportPage() {
  const { data } = useQuery({
    queryKey: ['admin-dashboard'],
    queryFn: () => api<DashboardMetrics>('/api/admin/dashboard'),
    refetchInterval: 60_000,
  })

  return (
    <div className="space-y-6">
      <PageHeader
        title="Support center"
        description="Internal support workflows. Moderation reports and privacy appeals are the primary escalation path today."
      />

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <Card>
          <CardHeader>
            <CardTitle>Open reports</CardTitle>
            <CardDescription>Trust &amp; Safety queue</CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-bold text-[#40dbf1]">{data?.trustSafety.openReports ?? '—'}</p>
            <Link to="/trust-safety/reports" className="inline-block mt-4">
              <Button variant="secondary" size="sm">Open queue</Button>
            </Link>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Assigned to you</CardTitle>
            <CardDescription>Your active cases</CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-bold">{data?.trustSafety.assignedToMe ?? '—'}</p>
            <Link to="/trust-safety/reports" className="inline-block mt-4">
              <Button variant="secondary" size="sm">View assigned</Button>
            </Link>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Appeals &amp; privacy</CardTitle>
            <CardDescription>Deletion &amp; appeal requests</CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-[#9aabbb] leading-relaxed">
              Privacy deletion and account appeals appear as reports with categories{' '}
              <Badge variant="muted">account_deletion</Badge> and <Badge variant="muted">appeal</Badge>.
            </p>
            <Link to="/trust-safety/appeals" className="inline-block mt-4">
              <Button variant="secondary" size="sm">Review appeals</Button>
            </Link>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Escalation workflow</CardTitle>
          <CardDescription>A consistent path from intake to resolution</CardDescription>
        </CardHeader>
        <CardContent>
          <ol className="support-workflow" aria-label="Support escalation workflow">
            <li><span>1</span><div><strong>Review the report</strong><p>Confirm the category, reporter context, and disclosed message snippet.</p></div></li>
            <li><span>2</span><div><strong>Open or join a case</strong><p>Merge duplicates so one investigation owns the decision and evidence.</p></div></li>
            <li><span>3</span><div><strong>Record the outcome</strong><p>Apply the least invasive action and leave an auditable reason for staff.</p></div></li>
          </ol>
          <div className="flex flex-wrap gap-2 mt-5">
            <Link to="/trust-safety/cases"><Button size="sm">Open cases</Button></Link>
            <Link to="/audit"><Button variant="secondary" size="sm">Review audit log</Button></Link>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
